"use client";

import { DatePickerProvider } from "@/app/components/common/DatePicker/DatePickerContext";
import HeroHeader from "@/app/components/common/HeroHeader";
import Search from "@/app/components/common/Search";
import SearchFlightsWidget from "@/app/components/common/SearchFlightsWidget/SearchFlightsWidget";
import { Switch } from "@/app/components/common/Switch/Switch";
import { Destination } from "@/types";
import { useTranslations, useLocale } from "next-intl";
import { useRef, type Dispatch, type SetStateAction } from "react";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import type { LocalizedText } from "@/app/api/utils/types/tenants";

function getLocalizedText(
  text: LocalizedText | undefined,
  locale: string
): string {
  if (!text) return "";
  return text[locale as keyof LocalizedText] || text.en || "";
}

type Props = {
  type: "hotels" | "flights";
  staticContentData?: {
    banner_image?: string;
    banner_heading?: string;
    banner_description?: string;
  };
  destination?: Destination | null;
  setDestination?: Dispatch<SetStateAction<Destination>>;
};

const SearchFlightsHero = ({
  type,
  staticContentData = {},
  destination,
  setDestination,
}: Props) => {
  const t = useTranslations();
  const locale = useLocale();
  const {
    name: tenantName,
    banner,
    flightBanner,
    showFlights,
  } = useTenantBranding();
  const calendarRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // flights type uses flightBanner; hotels type uses banner
  const activeBanner = type === "flights" ? flightBanner : banner;

  const bannerTitle = getLocalizedText(activeBanner?.title, locale);
  const bannerDescription = getLocalizedText(activeBanner?.description, locale);

  const bgUrl =
    staticContentData.banner_image ||
    activeBanner?.imageUrl ||
    "/assets/v2/images/sleap-hero-hotels.png";
  const headingText =
    staticContentData.banner_heading ??
    bannerTitle ??
    t("HomePage.title", { tenantName });
  const description =
    staticContentData.banner_description ??
    bannerDescription ??
    t("HomePage.unlockExclusiveWeb3");

  return (
    <div className="bg-gradient-to-r from-[#026492] via-[#0B8CA4] to-[#7999B0]">
      <div
        className="bg-cover bg-right w-full banner-background"
        style={{ backgroundImage: `url(${bgUrl})` }}
      >
        <div>
          <div className="container max-w-7xl mx-auto">
            <HeroHeader />

            <div className="flex flex-col-reverse md:flex-col">
              {showFlights && (
                <div className="flex justify-center mb-4 md:mb-8">
                  <Switch type={type} />
                </div>
              )}

              <div className="text-center mb-4 md:mb-8 md:w-5/12 md:mx-auto ">
                <span
                  className="block md:inline text-white text-4xl lg:text-5xl font-semibold font-switzer md:leading-[72px]"
                  style={{ letterSpacing: "-0.01em" }}
                >
                  {headingText}
                </span>
              </div>
            </div>

            {type === "hotels" ? (
              <>
                <div className="home-page-banner">
                  <DatePickerProvider>
                    <Search
                      searchRef={searchRef}
                      calendarRef={calendarRef}
                      setDestination={setDestination!}
                      destination={destination!}
                      isHomePage={true}
                    />
                  </DatePickerProvider>
                </div>
              </>
            ) : (
              <SearchFlightsWidget />
            )}
            <p className="text-sm font-medium text-white text-center m-0 mt-4 opacity-80 md:pb-32 pb-16 font-switzer">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFlightsHero;

